import { loadSession, refreshSession } from './cognito';

function apiBase() {
  const u = import.meta.env.VITE_API_URL?.replace(/\/$/, '');
  if (!u) throw new Error('VITE_API_URL mancante');
  return u;
}

async function withFreshToken<T>(fn: (idToken: string) => Promise<T>): Promise<T> {
  let s = loadSession();
  if (!s) throw new Error('Non autenticato');
  try {
    return await fn(s.idToken);
  } catch (e) {
    const msg = e instanceof Error ? e.message : '';
    if (msg.includes('401') || msg.includes('Unauthorized')) {
      s = await refreshSession(s.refreshToken);
      return await fn(s.idToken);
    }
    throw e;
  }
}

async function apiFetch(path: string, init: RequestInit = {}, idTokenOverride?: string) {
  const token = idTokenOverride ?? loadSession()?.idToken;
  if (!token) throw new Error('Non autenticato');
  const url = `${apiBase()}${path.startsWith('/') ? path : `/${path}`}`;
  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${token}`);
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  const res = await fetch(url, { ...init, headers });
  if (res.status === 401) {
    throw new Error('401 Unauthorized');
  }
  return res;
}

export async function apiJson<T>(path: string, init?: RequestInit): Promise<T> {
  return withFreshToken(async (idToken) => {
    const res = await apiFetch(path, init, idToken);
    const text = await res.text();
    if (!res.ok) {
      let errMsg = res.statusText;
      try {
        const j = JSON.parse(text) as { error?: string; message?: string };
        errMsg = j.error || j.message || errMsg;
      } catch {
        if (text) errMsg = text;
      }
      throw new Error(errMsg);
    }
    return text ? (JSON.parse(text) as T) : ({} as T);
  });
}

export type Category = {
  categoryId: string;
  name: string;
  icon?: string;
  type: 'in' | 'out';
  isSystem: boolean;
};

export type Expense = {
  expenseId: string;
  userId?: string;
  creatorDisplayName?: string | null;
  creatorAvatarUrl?: string | null;
  amount: number;
  currency: string;
  categoryId: string;
  date: string;
  time?: string | null;
  note?: string;
  recurringId?: string | null;
  createdAt: string;
};

export type RecurringTemplate = {
  recurringId: string;
  name: string;
  categoryId: string;
  defaultAmount: number;
  active: boolean;
  createdAt?: string;
};

export type RecurringMonthEntry = {
  recurringId: string;
  enabled: boolean;
  amount?: number;
};

export async function getCategories() {
  return apiJson<{ categories: Category[] }>('/categories');
}

export async function getExpenses(qs: Record<string, string>) {
  const p = `?${new URLSearchParams(qs)}`;
  return apiJson<{ expenses: Expense[] }>(`/expenses${p}`);
}

export async function createExpense(body: {
  amount: number;
  categoryId: string;
  date?: string;
  time?: string;
  note?: string;
  currency?: string;
}) {
  return apiJson<{
    expenseId: string;
    amount: number;
    categoryId: string;
    date: string;
    time?: string;
    note?: string;
  }>('/expenses', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function updateExpense(
  expenseId: string,
  body: {
    amount?: number;
    categoryId?: string;
    date?: string;
    time?: string;
    note?: string;
    currency?: string;
  }
) {
  return apiJson<unknown>(`/expenses/${encodeURIComponent(expenseId)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export async function deleteExpense(expenseId: string) {
  return apiJson<{ deleted: string }>(`/expenses/${encodeURIComponent(expenseId)}`, {
    method: 'DELETE',
  });
}

export async function getRecurring() {
  return apiJson<{ templates: RecurringTemplate[] }>('/recurring');
}

export async function createRecurring(body: {
  name: string;
  categoryId: string;
  defaultAmount: number;
  active?: boolean;
}) {
  return apiJson<RecurringTemplate>('/recurring', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function updateRecurring(
  recurringId: string,
  body: { name?: string; categoryId?: string; defaultAmount?: number; active?: boolean }
) {
  return apiJson<RecurringTemplate>(`/recurring/${encodeURIComponent(recurringId)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export async function deleteRecurring(recurringId: string) {
  return apiJson<{ deleted: string }>(`/recurring/${encodeURIComponent(recurringId)}`, {
    method: 'DELETE',
  });
}

export async function applyRecurringMonth(body: {
  year: number;
  month: number;
  entries: RecurringMonthEntry[];
}) {
  return apiJson<{ year: number; month: number; results: unknown[] }>('/recurring/month', {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export async function adminCheck() {
  return apiJson<{ admin: boolean }>('/admin/check');
}

export async function adminListCategories() {
  return apiJson<{ categories: Category[] }>('/admin/categories');
}

export async function adminCreateCategory(body: { name: string; icon?: string; type?: string }) {
  return apiJson<unknown>('/admin/categories', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function adminUpdateCategory(
  categoryId: string,
  body: { name?: string; icon?: string; type?: string }
) {
  return apiJson<unknown>(`/admin/categories/${encodeURIComponent(categoryId)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export async function adminDeleteCategory(categoryId: string) {
  return apiJson<unknown>(`/admin/categories/${encodeURIComponent(categoryId)}`, {
    method: 'DELETE',
  });
}
